import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Icon } from "./Icon.js";
import "./Button.css";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner in place of the leading icon and disables the button — the
   * caller doesn't need to also pass `disabled`. */
  loading?: boolean;
  icon?: ReactNode;
  iconTrailing?: ReactNode;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    loading = false,
    icon,
    iconTrailing,
    fullWidth = false,
    disabled,
    className,
    children,
    ...rest
  },
  ref
) {
  const classes = [
    "ui-btn",
    `ui-btn--${variant}`,
    `ui-btn--${size}`,
    fullWidth ? "ui-btn--full" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      ref={ref}
      type="button"
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <Icon name="spinner" size={size === "lg" ? 18 : 16} className="ui-btn__icon" />
      ) : (
        icon && <span className="ui-btn__icon">{icon}</span>
      )}
      {children && <span className="ui-btn__label">{children}</span>}
      {!loading && iconTrailing && <span className="ui-btn__icon">{iconTrailing}</span>}
    </button>
  );
});
